import type {
  IFilterMap,
  TFiltersParams,
  TTextValuePair,
} from '../../types/data_table';

import { arrayToCommaSeparated } from '../../utils/stringArrayUtils';

/**
 * Pure reducers over `params` — the flat `{ [filterId]: value }` bag that is
 * merged into every request the table makes.
 *
 * They all follow one rule: a filter that is *not* narrowing the results must
 * leave no key behind, so an empty input or an "everything selected" dropdown
 * removes its key rather than sending an empty value to the API.
 */

/** Returns a copy of `params` without `id`. */
const omitParam = (params: TFiltersParams, id: string): TFiltersParams => {
  const { [id]: _removed, ...rest } = params;
  return rest;
};

/**
 * `false` and `0` are legitimate filter values, so emptiness cannot be tested
 * with a plain truthiness check.
 */
const isApplicableValue = (value: unknown): boolean =>
  value === false || value === 0 || !!value;

/**
 * Applies a single-selection dropdown choice.
 *
 * @param option The chosen option, or `undefined` when the filter is cleared.
 */
export const withSelectParam = (
  params: TFiltersParams,
  id: string,
  option?: TTextValuePair
): TFiltersParams => {
  const rest = omitParam(params, id);
  const value = option?.value;

  return isApplicableValue(value)
    ? { ...rest, [id]: value as TFiltersParams[string] }
    : rest;
};

/**
 * Applies a multi-selection dropdown choice.
 *
 * Selecting every option means "no filter", so the key is dropped instead of
 * sending the full option list back to the API.
 *
 * @param selectedOptions Options currently ticked.
 * @param totalOptions    How many options the filter offers in total.
 * @param queryParamType  `comma-separated` sends `a,b`; otherwise an array.
 */
export const withMultiSelectParam = (
  params: TFiltersParams,
  id: string,
  selectedOptions: TTextValuePair[],
  totalOptions: number,
  queryParamType?: IFilterMap['multiSelectionParamType']
): TFiltersParams => {
  const rest = omitParam(params, id);

  if (selectedOptions.length === totalOptions) return rest;

  const values = selectedOptions.map((option) => option.value);

  return {
    ...rest,
    [id]:
      queryParamType === 'comma-separated'
        ? arrayToCommaSeparated(values)
        : values,
  };
};

/**
 * Applies a text, phone or search input value, dropping the key when the
 * input is empty.
 */
export const withFieldParam = (
  params: TFiltersParams,
  id: string,
  value?: string
): TFiltersParams => {
  const rest = omitParam(params, id);

  return value ? { ...rest, [id]: value } : rest;
};

/**
 * Sorts freshly picked options back into the order the filter declares them,
 * so the dropdown's summary text ("Visa, +2") stays stable no matter which
 * order the user ticked the boxes in.
 */
export const sortOptionsLikeFilter = (
  selectedOptions: TTextValuePair[],
  filterOptions: TTextValuePair[]
): TTextValuePair[] =>
  [...selectedOptions].sort(
    (a, b) =>
      filterOptions.findIndex((option) => option.value === a.value) -
      filterOptions.findIndex((option) => option.value === b.value)
  );
